import Button from 'components/Button';
import LinkButton from 'components/LinkButton';
import Pill from 'components/Pill';
import TooltipWrapper from 'components/Tooltip';
import arrow from 'components/Tooltip/TooltipArrow';
import {FC, ReactNode} from 'react';
import {c} from 'ttag';
import {PermissionResponse} from 'types/permissions';

const Row = ({label, value}: {label: string; value: ReactNode}) => (
    <div className="text-sm">
        <div className="font-bold">{label}</div>
        <div className="flex flex-wrap gap-1 break-all">{value}</div>
    </div>
);

type PopoverContainerProps = {
    children: ReactNode;
    onClose?: () => void;
    title?: string;
};
const PopoverContainer: FC<PopoverContainerProps> = ({
    children,
    onClose,
    title,
}) => (
    <div className="flex flex-col items-center drop-shadow-md">
        <span className="text-grey-200">{arrow['bottom']}</span>
        <div className="flex w-72 flex-col gap-2 rounded border border-grey-200 bg-white">
            <div className="flex justify-between bg-grey-200 px-2 py-1 font-bold">
                <span className="text-sm">{title || '-'}</span>
                <Button
                    icon="close_small"
                    kind="tertiary"
                    onClick={onClose}
                    size="xs"
                />
            </div>
            {children}
        </div>
    </div>
);

type RenderDocumentProps = {
    date?: string;
    dateDue?: string;
    fileUrl?: string;
    notes?: string;
    permissions?: {
        canViewPdf: PermissionResponse;
    };
    tags?: string[];
    template?: string;
};
const RenderDocument: FC<RenderDocumentProps> = ({
    date,
    dateDue,
    fileUrl,
    notes,
    permissions,
    tags = [],
    template,
}) => (
    <div className="flex flex-col gap-2 px-2 pb-2">
        <div className="grid grid-cols-2">
            {date && (
                <Row label={c('supersend-popover').t`Date`} value={date} />
            )}
            {dateDue && (
                <Row
                    label={c('supersend-popover').t`Date Due`}
                    value={dateDue}
                />
            )}
        </div>
        <Row
            label={c('supersend-popover').t`Tags`}
            value={
                tags.length === 0 ? (
                    <div className="text-grey-400">-</div>
                ) : (
                    tags.map((value) => (
                        <Pill
                            color="grey"
                            isTruncated={false}
                            key={value}
                            label={value}
                        />
                    ))
                )
            }
        />
        <Row
            label={c('supersend-popover').t`Template`}
            value={template || '-'}
        />
        <Row label={c('supersend-popover').t`Notes`} value={notes || '-'} />
        <div>
            <TooltipWrapper message={permissions?.canViewPdf.failureReason}>
                <LinkButton
                    disabled={!permissions?.canViewPdf.allowed}
                    href={fileUrl}
                    kind="secondary"
                    size="xs"
                >
                    {c('supersend-popover').t`View PDF`}
                </LinkButton>
            </TooltipWrapper>
        </div>
    </div>
);

type RenderPdfFileProps = {
    fileUrl?: string;
    permissions?: {
        canViewPdf: PermissionResponse;
    };
};
const RenderPdfFile: FC<RenderPdfFileProps> = ({fileUrl, permissions}) => (
    <TooltipWrapper message={permissions?.canViewPdf.failureReason}>
        <div className="px-2 pb-2">
            <LinkButton
                className="text-sm"
                disabled={!permissions?.canViewPdf.allowed}
                href={fileUrl}
                kind="secondary"
                size="xs"
            >
                {c('supersend-popover').t`View PDF`}
            </LinkButton>
        </div>
    </TooltipWrapper>
);

type RenderZipFileProps = {
    fileUrl?: string;
    permissions?: {
        canViewPdf: PermissionResponse;
    };
};
const RenderZipFile: FC<RenderZipFileProps> = ({fileUrl, permissions}) => (
    <TooltipWrapper message={permissions?.canViewPdf.failureReason}>
        <div className="px-2 pb-2">
            <LinkButton
                className="text-sm"
                disabled={!permissions?.canViewPdf.allowed}
                href={fileUrl}
                kind="secondary"
                size="xs"
            >
                {c('supersend-popover').t`Download`}
            </LinkButton>
        </div>
    </TooltipWrapper>
);

type SupersendPopoverProps = {
    date?: string;
    dateDue?: string;
    fileUrl?: string | undefined;
    kind: 'document' | 'pdf-file' | 'zip-file';
    notes?: string;
    onClose?: () => void;
    permissions?: {
        canViewPdf: PermissionResponse;
    };
    tags?: string[];
    template?: string;
    title?: string;
};

const SupersendPopover: FC<SupersendPopoverProps> = ({kind, ...props}) => {
    const content = (() => {
        if (kind === 'document') {
            return <RenderDocument {...props} />;
        }
        if (kind === 'pdf-file') {
            return <RenderPdfFile {...props} />;
        }
        if (kind === 'zip-file') {
            return <RenderZipFile {...props} />;
        }
        throw new Error('Unhandled Kind');
    })();

    return (
        <PopoverContainer onClose={props.onClose} title={props.title}>
            {content}
        </PopoverContainer>
    );
};
export default SupersendPopover;
